import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/api/auth";
import { Panel } from "@/components/Panel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  GLS_KLASSEN, GT150_AB_MM, NETZ_ROLLEN, NOTIZ_MAX, SCHWELLE_MAX, SCHWELLE_MIN, VORSCHLAG_AB_MM, VERSAND_FORMATE,
  useLoescheVersandregel, useSetVersandregel, useVersandregel,
  type GlsKlasse, type NetzRolle, type VersandFormat, type VersandRegelAntwort, type VersandRegelWerte,
} from "@/api/versandregel";
import { VersandformatFelder } from "./VersandformatFelder";
import { staffelEntwurf, staffelGueltig, type VersandStufenEntwurf } from "./versandstaffel";

// Artikel-Eigenschaften und optionale GLS-Vorgaben erben unabhängig voneinander.

const selectKlasse = "h-9 min-w-0 rounded-md border border-line bg-panel px-2 text-sm font-normal text-ink disabled:opacity-60";
const feldKlasse = "flex flex-col gap-1 text-xs font-semibold text-ink-2";

function fmtZeit(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short", timeZone: "Europe/Berlin" });
}

function regelText(r: VersandRegelWerte): string {
  const teile: string[] = [];
  const klasse = GLS_KLASSEN.find((k) => k.code === r.glsKlasse);
  if (klasse && r.glsAbMenge !== null) teile.push(`${klasse.anzeige} ab ${r.glsAbMenge} Stück`);
  if (r.netzRolle === "netz") teile.push("Netz");
  if (r.netzRolle === "zubehoer") teile.push("Zubehör");
  if (r.mindestFormat) teile.push(VERSAND_FORMATE.find(f => f.code === r.mindestFormat)!.anzeige);
  for (const s of staffelEntwurf(r)) teile.push(`${s.format === "paket" ? "Paket" : "Warenpost / Kleinpaket"} ab ${s.abMenge} Stück`);
  return teile.join(" · ");
}

function Stand({ daten, stockItemId }: { daten: VersandRegelAntwort; stockItemId: number }) {
  const geerbt = (daten.quellen ?? []).filter((q) => q.ownerId !== stockItemId);
  return (
    <div className="flex flex-col gap-1 text-xs text-ink-2">
      {daten.eigene ? (
        <p>Eigene Regel · geändert von {daten.eigene.geaendertVon} am {fmtZeit(daten.eigene.geaendertAm)}</p>
      ) : null}
      {geerbt.map((q, index) => (
        <p key={`${q.ownerId}-${index}`}>
          Geerbt von Artikel {q.ownerId} · {q.units} Stück je Verkaufseinheit · {regelText(q.regel)}
        </p>
      ))}
      {!daten.eigene && geerbt.length === 0 ? (
        <p>Keine Regel — Versand nach Gewicht und Maß wie bisher.</p>
      ) : null}
    </div>
  );
}

export function VersandregelPanel({ stockItemId, sku, laengsteSeiteMm }: {
  stockItemId: number;
  sku: string;
  laengsteSeiteMm: number | null;
}) {
  const auth = useAuth();
  const darfAendern = !!auth.user?.permissions?.article_general;
  const { data, isLoading } = useVersandregel(sku);
  const speichern = useSetVersandregel(sku);
  const loeschen = useLoescheVersandregel(sku);
  const beschaeftigt = speichern.isPending || loeschen.isPending;

  const [klasse, setKlasse] = useState<GlsKlasse | "">("");
  const [schwelle, setSchwelle] = useState("");
  const [rolle, setRolle] = useState<NetzRolle | "">("");
  const [notiz, setNotiz] = useState("");
  const [format, setFormat] = useState<VersandFormat | "">("");
  const [stufen, setStufen] = useState<VersandStufenEntwurf[]>([]);

  useEffect(() => {
    if (!data) return;
    setKlasse(data.eigene?.glsKlasse ?? "");
    setSchwelle(data.eigene?.glsAbMenge != null ? String(data.eigene.glsAbMenge) : "");
    setRolle(data.eigene?.netzRolle ?? "");
    setNotiz(data.eigene?.notiz ?? "");
    setFormat(data.eigene?.mindestFormat ?? "");
    setStufen(staffelEntwurf(data.eigene));
  }, [data]);

  const schwelleZahl = Number(schwelle);
  const schwelleGueltig = Number.isInteger(schwelleZahl) && schwelleZahl >= SCHWELLE_MIN && schwelleZahl <= SCHWELLE_MAX;
  const paarOk = klasse === "" || (schwelle !== "" && schwelleGueltig);
  const stufenOk = staffelGueltig(stufen);
  const inhaltOk = klasse !== "" || rolle !== "" || format !== "";
  const eigene = data?.eigene ?? null;
  const geaendert = !!data && (
    klasse !== (eigene?.glsKlasse ?? "")
    || (klasse !== "" && schwelle !== String(eigene?.glsAbMenge ?? ""))
    || rolle !== (eigene?.netzRolle ?? "")
    || notiz.trim() !== (eigene?.notiz ?? "")
    || format !== (eigene?.mindestFormat ?? "")
    || JSON.stringify(stufen) !== JSON.stringify(staffelEntwurf(eigene))
  );
  const kannSpeichern = darfAendern && geaendert && paarOk && stufenOk && inhaltOk && !beschaeftigt;

  // Vorschlag nur, wo noch keine Schwelle wirkt (eigene oder geerbte) und die Maske leer ist.
  const schwelleWirkt = !!data && (eigene?.glsKlasse != null || (data.quellen ?? []).some((q) => q.regel.glsKlasse !== null));
  const vorschlag: GlsKlasse | null = laengsteSeiteMm !== null && laengsteSeiteMm > VORSCHLAG_AB_MM && !schwelleWirkt && klasse === ""
    ? (laengsteSeiteMm > GT150_AB_MM ? "gls_uebergroesse_gt150" : "gls_uebergroesse_150")
    : null;

  function absenden() {
    speichern.mutate(
      {
        gls_ab_menge: klasse === "" ? null : schwelleZahl,
        gls_klasse: klasse === "" ? null : klasse,
        netz_rolle: rolle === "" ? null : rolle,
        notiz: notiz.trim() === "" ? null : notiz.trim(),
        ...(format !== "" || eigene?.mindestFormat != null ? {
          mindest_format: format === "" ? null : format,
          paket_ab_menge: null,
          format_stufen: format === "" ? null : stufen.map(s => ({ abMenge: Number(s.abMenge), format: s.format })),
        } : {}),
      },
      {
        onSuccess: () => toast.success("Versandregel gespeichert"),
        onError: (err) => toast.error(err instanceof Error ? err.message : "Speichern fehlgeschlagen"),
      },
    );
  }

  function entfernen(bereich: "gls" | "format") {
    loeschen.mutate(bereich, {
      onSuccess: () => toast.success("Eigene Regel entfernt"),
      onError: (err) => toast.error(err instanceof Error ? err.message : "Entfernen fehlgeschlagen"),
    });
  }

  return (
    <Panel title="Versandeigenschaften" subtitle="Verpackungsbedarf, Mengen und optionale GLS-Vorgaben">
      {isLoading ? (
        <p className="text-xs text-ink-2">Lädt …</p>
      ) : !data ? (
        <p className="text-xs text-ink-2">Versandregel nicht verfügbar.</p>
      ) : (
        <div className="flex flex-col gap-3">
          <VersandformatFelder format={format} stufen={stufen} gueltig={stufenOk}
            disabled={!darfAendern || beschaeftigt} onStufen={setStufen}
            onFormat={wert => { setFormat(wert); if (wert === "" || wert === "paket") setStufen([]); }} />
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_8rem]">
            <label className={feldKlasse}>
              GLS-Klasse
              <select
                aria-label="GLS-Klasse"
                className={selectKlasse}
                value={klasse}
                disabled={!darfAendern || beschaeftigt}
                onChange={(e) => {
                  const neu = e.target.value as GlsKlasse | "";
                  setKlasse(neu);
                  if (neu === "") setSchwelle("");
                }}
              >
                <option value="">— keine GLS-Pflicht —</option>
                {GLS_KLASSEN.map((k) => (
                  <option key={k.code} value={k.code}>{k.anzeige}</option>
                ))}
              </select>
            </label>
            <label className={feldKlasse}>
              GLS ab Stück
              <Input
                aria-label="GLS ab Stück"
                type="number"
                inputMode="numeric"
                min={SCHWELLE_MIN}
                max={SCHWELLE_MAX}
                step={1}
                value={schwelle}
                disabled={!darfAendern || beschaeftigt || klasse === ""}
                aria-invalid={klasse !== "" && !paarOk}
                onChange={(e) => setSchwelle(e.target.value)}
              />
            </label>
          </div>
          <label className={feldKlasse}>
            Netz-Rolle
            <select
              aria-label="Netz-Rolle"
              className={selectKlasse}
              value={rolle}
              disabled={!darfAendern || beschaeftigt}
              onChange={(e) => setRolle(e.target.value as NetzRolle | "")}
            >
              <option value="">— kein Netz —</option>
              {NETZ_ROLLEN.map((r) => (
                <option key={r.code} value={r.code}>{r.anzeige}</option>
              ))}
            </select>
          </label>
          <label className={feldKlasse}>
            Notiz
            <Textarea
              aria-label="Notiz"
              maxLength={NOTIZ_MAX}
              value={notiz}
              disabled={!darfAendern || beschaeftigt}
              placeholder="z. B. Rolle 160 cm, DHL Sperrgut"
              onChange={(e) => setNotiz(e.target.value)}
            />
          </label>
          {vorschlag ? (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-line px-3 py-2 text-xs text-ink-2">
              <span>
                Längste Seite {Math.round(laengsteSeiteMm! / 10)} cm — Vorschlag:{" "}
                {GLS_KLASSEN.find((k) => k.code === vorschlag)!.anzeige} ab 1 Stück. Gilt erst nach „Speichern“.
              </span>
              {darfAendern ? (
                <Button type="button" size="sm" variant="outline" disabled={beschaeftigt}
                  onClick={() => { setKlasse(vorschlag); setSchwelle("1"); }}>
                  Vorschlag übernehmen
                </Button>
              ) : null}
            </div>
          ) : null}
          <Stand daten={data} stockItemId={stockItemId} />
          {darfAendern ? (
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" disabled={!kannSpeichern} onClick={absenden}>
                Versandregel speichern
              </Button>
              {eigene && (eigene.glsAbMenge != null || eigene.netzRolle != null) ? (
                <Button type="button" size="sm" variant="outline" disabled={beschaeftigt} onClick={() => entfernen("gls")}>
                  Eigene GLS-Regel entfernen
                </Button>
              ) : null}
              {eigene?.mindestFormat != null ? (
                <Button type="button" size="sm" variant="outline" disabled={beschaeftigt} onClick={() => entfernen("format")}>
                  Eigene Formatregel entfernen
                </Button>
              ) : null}
            </div>
          ) : (
            <p className="text-xs text-ink-2">Ändern nur mit Artikelrecht.</p>
          )}
          {darfAendern && !inhaltOk && eigene ? (
            <p className="text-xs text-ink-2">Zum Übernehmen der geerbten Angaben die eigene Regel entfernen.</p>
          ) : null}
        </div>
      )}
    </Panel>
  );
}
